import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import Header from "./Header";
import NetWorthCard from "./NetWorthCard";
import WhatYouOwn from "./WhatYouOwn";
import PricingModal from "./PricingModal";
import Assets from "@/features/assets/Assets";
import Liabilities from "@/features/liabilities/Liabilities";
import Expenses from "@/features/expenses/Expenses";
import { formatCurrency } from "@vaulzo/core/constants";
import { useDashboardData, useSnapshots, useExchangeRates } from "@vaulzo/core/hooks/usePortfolioQueries";
import { applyTheme, getTheme } from "@/theme";

/**
 * Authenticated shell: fetches its own data via query hooks and takes
 * no props at all — auth actions come from useAuth (used in Header).
 */
export default function Dashboard() {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem("dashboardActiveTab") || "overview";
  });
  const [pricingOpen, setPricingOpen] = useState(false);
  const [dateRange, setDateRange] = useState("all");
  // Sub-tab the Assets page should open on; set by "What you own" rows.
  const [assetsSubTab, setAssetsSubTab] = useState(null);
  const goTo = (tab, subTab = null) => {
    setAssetsSubTab(subTab);
    setActiveTab(tab);
  };

  useEffect(() => {
    localStorage.setItem("dashboardActiveTab", activeTab);
  }, [activeTab]);

  // Light / dark applies while signed in; the landing and sign-in pages stay light.
  useEffect(() => {
    applyTheme(getTheme());
    return () => applyTheme("light");
  }, []);

  const dashboardQuery = useDashboardData();
  const snapshotsQuery = useSnapshots(dateRange);
  const ratesQuery = useExchangeRates();

  const dash = dashboardQuery.data;

  // Fresh prices also rewrite today's snapshot: reload the chart once a
  // background price refresh finishes.
  const queryClient = useQueryClient();
  const wasRefreshing = useRef(false);
  useEffect(() => {
    const refreshing = !!dash?.prices_refreshing;
    if (wasRefreshing.current && !refreshing) {
      queryClient.invalidateQueries({ queryKey: ["snapshots"] });
    }
    wasRefreshing.current = refreshing;
  }, [dash?.prices_refreshing, queryClient]);
  const assets = dash?.assets ?? [];
  const liabilities = dash?.liabilities ?? [];

  const isInitialLoad = dashboardQuery.isLoading || (snapshotsQuery.isLoading && !snapshotsQuery.data);
  // Range switches keep the previous chart visible with a spinner overlay.
  const isSnapshotsLoading = snapshotsQuery.isFetching && !isInitialLoad;

  return (
    <div className="min-h-[100dvh] bg-vaulzo-bg pb-[120px] text-vaulzo-ink">
      <Header
        activeTab={activeTab}
        onTabChange={goTo}
        onUpgrade={() => setPricingOpen(true)}
        pricesRefreshing={!!dash?.prices_refreshing}
        updatedAt={dashboardQuery.dataUpdatedAt || null}
      />

      {isInitialLoad ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-vaulzo-accent" aria-hidden />
        </div>
      ) : dashboardQuery.isError && !dash ? (
        // Nothing cached and the API is unreachable (usually still waking up):
        // say so instead of rendering ₹0.
        <div className="mx-auto flex min-h-[50vh] max-w-[800px] flex-col items-center justify-center px-6 text-center">
          <div className="text-[15px] font-semibold text-vaulzo-ink">Couldn't reach Vaulzo</div>
          <div className="mt-2 max-w-[360px] text-sm text-vaulzo-faint">
            The server may be waking up. Your data is safe; try again in a moment.
          </div>
          <button
            type="button"
            onClick={() => dashboardQuery.refetch()}
            disabled={dashboardQuery.isFetching}
            className="mt-6 rounded-[10px] bg-vaulzo-accent px-[18px] py-[10px] text-sm font-semibold text-vaulzo-on-fill transition-colors hover:bg-vaulzo-accent-hover disabled:opacity-60"
          >
            {dashboardQuery.isFetching ? "Retrying…" : "Retry"}
          </button>
        </div>
      ) : activeTab === "overview" ? (
        <main className="mx-auto max-w-[800px] px-6 pt-[72px]">
          <NetWorthCard
            netWorth={dash?.net_worth ?? 0}
            snapshots={snapshotsQuery.data ?? []}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            exchangeRates={ratesQuery.data}
            isSnapshotsLoading={isSnapshotsLoading}
            historyFailed={snapshotsQuery.isError && !snapshotsQuery.data}
            onRetryHistory={() => snapshotsQuery.refetch()}
          />

          <WhatYouOwn assets={assets} onSelect={(subTab) => goTo("assets", subTab)} />

          {/* Assets / Liabilities summary footer */}
          <div className="mt-12 h-px bg-vaulzo-hairline" />
          <div className="flex">
            <button
              onClick={() => goTo("assets")}
              className="flex-1 border-r border-vaulzo-hairline pr-6 pt-[26px] text-left"
            >
              <div className="mb-2 text-[13px] text-vaulzo-fainter">
                Assets <span className="text-vaulzo-accent">→</span>
              </div>
              <div className="num text-[24px] font-bold tracking-[-0.02em] text-vaulzo-ink">
                {formatCurrency(dash?.total_assets ?? 0)}
              </div>
            </button>
            <button
              onClick={() => goTo("liabilities")}
              className="flex-1 pl-6 pt-[26px] text-right"
            >
              <div className="mb-2 text-[13px] text-vaulzo-fainter">
                Liabilities <span className="text-vaulzo-accent">→</span>
              </div>
              <div className="num text-[24px] font-bold tracking-[-0.02em] text-vaulzo-danger">
                {formatCurrency(dash?.total_liabilities ?? 0)}
              </div>
            </button>
          </div>
        </main>
      ) : activeTab === "assets" ? (
        <Assets assets={assets} invested={dash?.invested} initialSubTab={assetsSubTab} />
      ) : activeTab === "expenses" ? (
        <Expenses />
      ) : (
        <Liabilities liabilities={liabilities} />
      )}

      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
    </div>
  );
}
